import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FC, ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, BarChart3, Bot, Brain, Settings,
  Moon, PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen, Sun
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';

import { AuthMenu } from '../components/AuthMenu';
import { ConnectionStatus } from '../components/ConnectionStatus';
import { NotificationCenter } from '../components/NotificationCenter';
import { RightSidebar } from '../components/RightSidebar';
import { RoutePrefetch } from '../components/RoutePrefetch';
import { StatusBar } from '../components/StatusBar';
import { useAlerts } from '../hooks/useAlerts';
import { useAgents } from '../hooks/useAgents';
import { usePortfolio } from '../hooks/usePortfolio';
import { useSystemHealth } from '../hooks/useSystemHealth';
import { useIndicatorSpecsFailedCatalog, useStrategySpecsFailedCatalog } from '../hooks/useTradingSpecs';
import { useTheme } from '../hooks/useTheme';
import { cn } from '../lib/utils';
import {
  failedReadinessChecks,
  humanizeBuildFailureNotification,
  humanizeConnectivityNotification,
  humanizeExecutionNotification,
  humanizeHealthNotification,
  humanizeReadinessNotification,
  humanizeSignalNotification,
} from '../shared/notifications';
import { useAppStore } from '../shared/store/appStore';
import { useNotificationStore } from '../shared/store/notificationStore';
import { WS_CHANNELS } from '../shared/config/constants';
import { QK } from '../shared/model/queryKeys';
import { useBusChannel, useBusSocket, useWebSocketStatus } from '../shared/ws/useBusSocket';

import type { AgentResponse } from '../shared/api/generated';

interface PortfolioData {
  balance?: number;
  dailyPnL?: number;
  unrealizedPnL?: number;
  marginUsed?: number;
}

interface PositionData {
  symbol: string;
  side?: string;
  position_side?: string;
  positionSide?: string;
  quantity?: number;
  positionAmt?: number;
  current_price?: number;
  currentPrice?: number;
  mark_price?: number;
  markPrice?: number;
  unrealized_pnl?: number;
  unrealizedPnl?: number;
}

interface SidebarItem {
  icon: typeof Activity;
  label: string;
  path: string;
  badge: string | null;
}

interface TradingLayoutProps { children?: ReactNode }

export const TradingLayout: FC<TradingLayoutProps> = ({ children }) => {
  const { t } = useTranslation(['common', 'layout']);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
  const [now, setNow] = useState<Date>(new Date());
  const { theme, toggleTheme } = useTheme();
  const locale = useAppStore((state) => state.locale);
  const setLocale = useAppStore((state) => state.setLocale);
  const queryClient = useQueryClient();
  const pushNotification = useNotificationStore((s) => s.push);
  useBusSocket({ autoConnect: true });
  const { isConnected } = useWebSocketStatus();
  const { agents } = useAgents();
  const active = useMemo(() => 
    agents?.find((a: AgentResponse) => a?.isRunning) || agents?.[0],
    [agents]
  );
  const agentId = active?.id ?? '';
  const { portfolio, positions, isLoading: portfolioLoading, error: portfolioError, refetch: refetchPortfolio } = usePortfolio(agentId);
  const {
    health,
    readiness,
    resources,
    liveness,
    apiUnavailable,
    resourcesError,
    isLoading: systemHealthLoading
  } = useSystemHealth();
  // Колокольчик — канал уведомлений: берём все активные алерты, а не три свежих
  // (при пяти одновременных два старых иначе не появились бы никогда).
  const { data: alertsData } = useAlerts(50);
  const { data: failedStrategies = [] } = useStrategySpecsFailedCatalog();
  const { data: failedIndicators = [] } = useIndicatorSpecsFailedCatalog();

  useBusChannel(
    agentId ? WS_CHANNELS.TRADING_SIGNAL(agentId) : '',
    useCallback((event: unknown) => {
      if (!event || typeof event !== 'object') return;
      const payload = (event as { payload?: unknown }).payload ?? event;
      if (!payload || typeof payload !== 'object') return;
      const p = payload as Record<string, unknown>;
      const signalType = String(p.signal_type ?? p.side ?? 'signal').toUpperCase();
      const symbol = String(p.symbol ?? '').toUpperCase();
      const notification = humanizeSignalNotification(signalType, symbol);
      pushNotification({
        severity: notification.severity,
        source: 'signal',
        title: notification.title,
        message: notification.message,
        ts: Number(p.timestamp ?? Date.now()),
        dedupKey: `signal:${agentId}:${signalType}:${symbol}`,
        link: '/agents',
      });
    }, [agentId, pushNotification]),
    { enabled: Boolean(agentId), snapshot: false },
  );

  useBusChannel(
    agentId ? WS_CHANNELS.TRADING_EXECUTION(agentId) : '',
    useCallback((event: unknown) => {
      if (!event || typeof event !== 'object') return;
      const payload = (event as { payload?: unknown }).payload ?? event;
      if (!payload || typeof payload !== 'object') return;
      const p = payload as Record<string, unknown>;
      const statusRaw = String(p.status ?? '').toUpperCase();
      const symbol = String(p.symbol ?? '').toUpperCase();
      const notification = humanizeExecutionNotification(statusRaw, symbol);
      pushNotification({
        severity: notification.severity,
        source: 'execution',
        title: notification.title,
        message: notification.message,
        ts: Number(p.timestamp ?? Date.now()),
        dedupKey: `exec:${agentId}:${statusRaw}:${String(p.client_order_id ?? '')}`,
        link: '/agents',
      });
    }, [agentId, pushNotification]),
    { enabled: Boolean(agentId), snapshot: false },
  );

  // Очищаем кэш portfolio при изменении agentId (ключи = QK / usePortfolio)
  useEffect(() => {
    if (agentId) {
      queryClient.invalidateQueries({ queryKey: QK.portfolio(agentId) });
      queryClient.invalidateQueries({ queryKey: QK.positions(agentId) });
    }
  }, [agentId, queryClient]);

  useEffect(() => {
    const healthStatus = String(health?.status || '').toLowerCase();
    if (healthStatus === 'degraded' || healthStatus === 'not_ready') {
      const notification = humanizeHealthNotification(healthStatus);
      pushNotification({
        severity: notification.severity,
        source: 'health',
        title: notification.title,
        message: notification.message,
        ts: Date.now(),
        dedupKey: `health:${healthStatus}`,
        link: '/statistics',
      });
    }
  }, [health?.status, pushNotification]);

  // Связь с API: healthz не отвечает — критично; вернулась после потери — информируем.
  const apiWasUnavailableRef = useRef(false);
  useEffect(() => {
    if (apiUnavailable === apiWasUnavailableRef.current) return;
    apiWasUnavailableRef.current = apiUnavailable;
    const state = apiUnavailable ? 'lost' : 'restored';
    const notification = humanizeConnectivityNotification(state);
    pushNotification({
      severity: notification.severity,
      source: 'health',
      title: notification.title,
      message: notification.message,
      ts: Date.now(),
      dedupKey: `health:api-${state}`,
      link: '/statistics',
    });
  }, [apiUnavailable, pushNotification]);

  // Readiness: какие именно зависимости сервера не готовы (Kafka, PostgreSQL, ...).
  const failedChecksKey = readiness?.status === 'not_ready'
    ? failedReadinessChecks(readiness.checks).join(',')
    : '';
  useEffect(() => {
    if (!failedChecksKey) return;
    const notification = humanizeReadinessNotification(failedChecksKey.split(','));
    pushNotification({
      severity: notification.severity,
      source: 'health',
      title: notification.title,
      message: notification.message,
      ts: Date.now(),
      dedupKey: `health:not-ready:${failedChecksKey}`,
      link: '/statistics',
    });
  }, [failedChecksKey, pushNotification]);

  const processedAlertIdsRef = useRef(new Set<string>());
  useEffect(() => {
    const list = alertsData?.alerts || [];
    for (const a of list) {
      if (a.severity !== 'critical' && a.severity !== 'warning') continue;
      if (processedAlertIdsRef.current.has(a.id)) continue;
      processedAlertIdsRef.current.add(a.id);
      pushNotification({
        severity: a.severity === 'critical' ? 'critical' : 'warning',
        source: 'system',
        title: a.title,
        message: a.description,
        ts: a.timestamp ? new Date(a.timestamp).getTime() : Date.now(),
        dedupKey: `alert:${a.id}`,
        link: '/statistics',
      });
    }
  }, [alertsData?.alerts, pushNotification]);

  useEffect(() => {
    if ((failedStrategies?.length || 0) > 0) {
      const notification = humanizeBuildFailureNotification('strategy', failedStrategies.length);
      pushNotification({
        severity: notification.severity,
        source: 'build',
        title: notification.title,
        message: notification.message,
        ts: Date.now(),
        dedupKey: `build:strategy:failed:${failedStrategies.length}`,
        link: '/strategies',
      });
    }
  }, [failedStrategies, pushNotification]);

  useEffect(() => {
    if ((failedIndicators?.length || 0) > 0) {
      const notification = humanizeBuildFailureNotification('indicator', failedIndicators.length);
      pushNotification({
        severity: notification.severity,
        source: 'build',
        title: notification.title,
        message: notification.message,
        ts: Date.now(),
        dedupKey: `build:indicator:failed:${failedIndicators.length}`,
        link: '/strategies',
      });
    }
  }, [failedIndicators, pushNotification]);

  // Мемоизация обработчиков для предотвращения лишних ре-рендеров
  const handleLeftSidebarToggle = useCallback(() => {
    setLeftSidebarOpen((prev) => !prev);
  }, []);

  const handleRightSidebarToggle = useCallback(() => {
    setRightSidebarOpen((prev) => !prev);
  }, []);

  // persist UI state
  useEffect(() => {
    const l = localStorage.getItem('ui:leftSidebarOpen');
    const r = localStorage.getItem('ui:rightSidebarOpen');
    if (l !== null) setLeftSidebarOpen(l === 'true');
    if (r !== null) setRightSidebarOpen(r === 'true');
  }, []);
  useEffect(() => {
    localStorage.setItem('ui:leftSidebarOpen', String(leftSidebarOpen));
  }, [leftSidebarOpen]);
  useEffect(() => {
    localStorage.setItem('ui:rightSidebarOpen', String(rightSidebarOpen));
  }, [rightSidebarOpen]);
  // clock tick
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const sidebarItems = useMemo<SidebarItem[]>(() => ([
    { icon: Activity, label: t('layout:nav.trading'), path: '/trading', badge: null },
    { icon: Bot, label: t('layout:nav.agents'), path: '/agents', badge: null },
    { icon: BarChart3, label: t('layout:nav.statistics'), path: '/statistics', badge: null },
    { icon: Brain, label: t('layout:nav.strategies'), path: '/strategies', badge: null },
    { icon: BarChart3, label: t('layout:nav.backtest'), path: '/backtest', badge: null },
    { icon: Settings, label: t('layout:nav.settings'), path: '/settings', badge: null },
  ]), [t]);

  const prefetchConfig = useMemo(
    () => ({
      '/trading': async (): Promise<void> => {
        // Предзагрузка данных для Trading Dashboard (при необходимости — добавлять точечно)
      },
      '/agents': async (): Promise<void> => {
        // Предзагрузка данных для Agents
      },
      '/statistics': async (): Promise<void> => {
        // Предзагрузка данных для Statistics
      },
    }),
    [],
  );

  // Мемоизация форматированных значений портфолио
  const portfolioData = useMemo(() => {
    const p = portfolio as PortfolioData | undefined;
    return {
      balance: Number(p?.balance ?? 0),
      dailyPnL: Number(p?.dailyPnL ?? p?.unrealizedPnL ?? 0),
      marginUsed: Number(p?.marginUsed ?? 0),
    };
  }, [portfolio]);

  // Мемоизация позиций для отображения
  const displayPositions = useMemo(() => {
    if (!Array.isArray(positions) || positions.length === 0) {
      return [];
    }
    return positions.slice(0, 5).map((p: PositionData) => {
      const sideLabel = (p.position_side ?? p.positionSide ?? p.side ?? '').toUpperCase();
      const price = Number(p.current_price ?? p.currentPrice ?? p.mark_price ?? p.markPrice ?? 0);
      const quantity = Number(p.quantity ?? p.positionAmt ?? 0);
      const notional = price * quantity;
      const change = Number(p.unrealized_pnl ?? p.unrealizedPnl ?? 0);
      return {
        symbol: p.symbol,
        sideLabel,
        quantity,
        notional,
        change,
        positive: change >= 0,
      };
    });
  }, [positions]);

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar — z-40 поверх основного контента и сайдбаров, чтобы колокольчик и кнопки были доступны */}
      <header className="relative z-40 h-14 shrink-0 border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLeftSidebarToggle}
              className="p-2 hover:bg-accent rounded-md transition-colors"
              aria-label={leftSidebarOpen ? t('layout:header.closeLeftSidebar') : t('layout:header.openLeftSidebar')}
            >
              {leftSidebarOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </button>
            
            <div className="flex items-center gap-2">
              <div className="text-xl font-bold bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent">
                AlgoSys
              </div>
              <ConnectionStatus isConnected={isConnected} latency={liveness?.latency_ms} />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* SystemAlerts убран: дублировал уведомления колокольчика (NotificationCenter). */}

            <div
              className="flex items-center rounded-md border border-border overflow-hidden"
              aria-label={t('common:language.label')}
              title={t('common:language.switch')}
            >
              {(['en', 'ru'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setLocale(value)}
                  className={cn(
                    'px-2 py-1 text-xs font-medium transition-colors',
                    locale === value ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent/60'
                  )}
                  aria-pressed={locale === value}
                >
                  {value.toUpperCase()}
                </button>
              ))}
            </div>
            
            <AuthMenu />

            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-accent rounded-md transition-colors"
              aria-label={t('common:theme.toggle')}
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            
            <NotificationCenter />
            
            <button
              onClick={handleRightSidebarToggle}
              className="p-2 hover:bg-accent rounded-md transition-colors"
              aria-label={rightSidebarOpen ? t('layout:header.closeRightSidebar') : t('layout:header.openRightSidebar')}
            >
              {rightSidebarOpen ? <PanelRightClose size={20} /> : <PanelRightOpen size={20} />}
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <AnimatePresence initial={false}>
          {leftSidebarOpen && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 240, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative z-40 border-r border-border bg-card/30"
            >
              <nav className="p-4 space-y-2">
                {sidebarItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center justify-between px-3 py-2 rounded-md transition-colors text-sm font-medium",
                        isActive ? "bg-accent" : "hover:bg-accent"
                      )
                    }
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={18} />
                      <span>{item.label}</span>
                    </div>
                    {item.badge && (
                      <span
                        className={cn(
                          "px-2 py-0.5 text-xs rounded-full",
                          item.badge === '!' ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"
                        )}
                      >
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </nav>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <main className="flex-1 overflow-auto relative">
          {children || <Outlet />}
        </main>

        {/* Right Sidebar */}
        <AnimatePresence initial={false}>
          {rightSidebarOpen && (
            <motion.aside
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 320, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative z-30 border-l border-border bg-card/30"
            >
              <RightSidebar
                agentId={agentId}
                portfolioData={portfolioData}
                displayPositions={displayPositions}
                portfolioLoading={portfolioLoading}
                portfolioError={portfolioError}
                onRefetchPortfolio={refetchPortfolio}
              />
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      <StatusBar
        now={now}
        liveness={liveness}
        resources={resources}
        systemHealthLoading={systemHealthLoading}
        apiUnavailable={apiUnavailable}
        resourcesError={resourcesError}
        agentRunning={Boolean(active?.isRunning)}
      />

      {/* Route prefetch for better UX */}
      <RoutePrefetch config={prefetchConfig} />
    </div>
  );
};
